import React, { useState, useEffect } from "react";
import API from "@/lib/api";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday } from "date-fns";
import { Check, Shield, Server, FileText, ChevronLeft, ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function RoutineMatrixPage() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingCheck, setPendingCheck] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [form, setForm] = useState({ category_name: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  
  const currentMonthStr = format(currentDate, "yyyy-MM");
  
  // Calculate days in current selected month
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  useEffect(() => {
    const fetchRoutines = async () => {
      setLoading(true);
      try {
        const res = await API.get(`/routines?month=${currentMonthStr}`);
        setRoutines(res.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load routines");
      } finally {
        setLoading(false);
      }
    };
    fetchRoutines();
  }, [currentMonthStr]);

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const requestToggle = (routineTaskId, dateStr, taskName) => {
    // Check if it's already checked, do nothing
    let alreadyChecked = false;
    routines.forEach(cat => {
      cat.tasks?.forEach(t => {
        if (t.id === routineTaskId && t.logs && t.logs[dateStr]) {
          alreadyChecked = true;
        }
      });
    });

    if (alreadyChecked) return;

    setPendingCheck({ routineTaskId, dateStr, taskName });
    setConfirmOpen(true);
  };

  const handleConfirmToggle = async () => {
    if (!pendingCheck) return;
    
    const { routineTaskId, dateStr } = pendingCheck;
    setConfirmOpen(false);
    setPendingCheck(null);

    // Optimistic update
    const previousRoutines = JSON.parse(JSON.stringify(routines));
    
    setRoutines(prev => prev.map(cat => ({
      ...cat,
      tasks: cat.tasks.map(task => {
        if (task.id === routineTaskId) {
          const newLogs = { ...task.logs };
          // check (optimistic)
          newLogs[dateStr] = {
            id: "temp",
            user_id: user?.id,
            user_name: user?.name,
            created_at: new Date().toISOString()
          };
          return { ...task, logs: newLogs };
        }
        return task;
      })
    })));

    try {
      const res = await API.post("/routines/toggle", {
        routine_task_id: routineTaskId,
        date: dateStr
      });
      
      // If it was an add, we update with the real data from server
      if (res.data.status === "added") {
        setRoutines(prev => prev.map(cat => ({
          ...cat,
          tasks: cat.tasks.map(task => {
            if (task.id === routineTaskId) {
              return {
                ...task,
                logs: {
                  ...task.logs,
                  [dateStr]: res.data.log
                }
              };
            }
            return task;
          })
        })));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update routine");
      // Revert optimistic update
      setRoutines(previousRoutines);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category_name.trim()) {
      toast.error("Nama tugas dan kategori harus diisi");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/routines/tasks", { 
        category_name: form.category_name.trim(), 
        name: form.name.trim() 
      });
      const res = await API.get(`/routines?month=${currentMonthStr}`);
      setRoutines(res.data || []);
      toast.success("Tugas berhasil ditambahkan");
      setDialogOpen(false);
      setForm({ category_name: "", name: "" });
    } catch (err) {
      toast.error("Gagal menambahkan tugas");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (id, name) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus kategori "${name}" dan semua tugas di dalamnya?`)) return;
    try {
      await API.delete(`/routines/categories/${id}`);
      const res = await API.get(`/routines?month=${currentMonthStr}`);
      setRoutines(res.data || []);
      toast.success("Kategori dihapus");
    } catch (err) {
      toast.error("Gagal menghapus kategori");
    }
  };

  const handleOpenAddTask = (categoryName = "") => {
    setForm({ category_name: categoryName, name: "" });
    setDialogOpen(true);
  };

  const handleDeleteTask = async (id, name) => {
    if (!window.confirm(`Apakah Anda yakin ingin menghapus tugas "${name}"?`)) return;
    try {
      await API.delete(`/routines/tasks/${id}`);
      const res = await API.get(`/routines?month=${currentMonthStr}`);
      setRoutines(res.data || []);
      toast.success("Tugas dihapus");
    } catch (err) {
      toast.error("Gagal menghapus tugas");
    }
  };

  const getCategoryIcon = (name) => {
    const n = name.toLowerCase();
    if (n.includes("backup") || n.includes("sistem") || n.includes("system")) return <Server className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
    if (n.includes("security") || n.includes("keamanan")) return <Shield className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />;
    return <FileText className="w-4 h-4 text-slate-600 dark:text-slate-400" />;
  };

  // Calculate some basic stats
  let totalCheckboxes = 0;
  let completedCheckboxes = 0;
  
  routines.forEach(cat => {
    if (cat.tasks && Array.isArray(cat.tasks)) {
      cat.tasks.forEach(task => {
        totalCheckboxes += daysInMonth.length;
        completedCheckboxes += Object.keys(task.logs || {}).length;
      });
    }
  });
  
  const completionRate = totalCheckboxes > 0 ? Math.round((completedCheckboxes / totalCheckboxes) * 100) : 0;

  return (
    <div className="space-y-6 max-w-[100vw] overflow-hidden" data-testid="routine-matrix-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Matriks Tugas Rutin
            </h1>
            <Button onClick={() => handleOpenAddTask("")} size="sm" variant="outline" className="h-8 gap-1">
              <Plus className="w-3.5 h-3.5" />
              Tambah Tugas
            </Button>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Lacak pekerjaan harian dan pemeliharaan operasional</p>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex items-center bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-1">
            <button onClick={handlePrevMonth} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors text-slate-600 dark:text-slate-300">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-4 text-sm font-semibold text-slate-700 dark:text-slate-200 min-w-[120px] text-center uppercase">
              {format(currentDate, "MMMM yyyy")}
            </div>
            <button onClick={handleNextMonth} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors text-slate-600 dark:text-slate-300">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="hidden md:block">
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-xl font-bold text-blue-600 dark:text-blue-400 leading-none">{completionRate}%</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Penyelesaian</div>
              </div>
              <div className="w-32 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-blue-600 dark:bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${completionRate}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <Card className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : routines.length === 0 ? (
            <div className="flex justify-center items-center h-64 text-slate-500 dark:text-slate-400">
              Tidak ada tugas rutin yang ditemukan untuk bulan ini.
            </div>
          ) : (
            <table className="w-full text-sm text-left border-collapse min-w-max">
              <thead>
                <tr className="bg-slate-50/80 dark:bg-slate-900/50">
                  <th className="sticky left-0 z-20 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur font-semibold text-slate-600 dark:text-slate-300 py-3 px-4 border-b border-r border-slate-200 dark:border-slate-700 w-64 min-w-[250px] uppercase tracking-wider text-[11px]">
                    Aktivitas & Sistem
                  </th>
                  {daysInMonth.map(day => {
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const today = isToday(day);
                    return (
                      <th 
                        key={day.toISOString()} 
                        className={`py-3 px-1 text-center border-b border-slate-200 dark:border-slate-700 min-w-[40px]
                          ${isWeekend ? 'bg-slate-100/50 dark:bg-slate-800/50 text-slate-400 dark:text-slate-500' : 'text-slate-600 dark:text-slate-300'}
                          ${today ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold' : 'font-medium'}
                        `}
                      >
                        <div className="flex flex-col items-center">
                          <span className="text-[10px] uppercase opacity-70">{format(day, "E").charAt(0)}</span>
                          <span className="text-xs">{format(day, "dd")}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                <TooltipProvider delayDuration={200}>
                  {routines.map((category) => (
                    <React.Fragment key={category.id}>
                      {/* Category Header */}
                      <tr className="bg-slate-50/50 dark:bg-slate-800/30 group/cat">
                        <td 
                          className="sticky left-0 z-10 bg-slate-50/95 dark:bg-slate-800/95 backdrop-blur py-3 px-4 border-b border-slate-200 dark:border-slate-700"
                        >
                          <div className="flex items-center justify-between w-full group/cat">
                            <div className="flex items-center gap-2 font-bold text-slate-800 dark:text-slate-200 text-xs uppercase tracking-wider">
                              {getCategoryIcon(category.name)}
                              {category.name}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover/cat:opacity-100 transition-opacity">
                              <button onClick={() => handleOpenAddTask(category.name)} className="p-1 text-slate-400 hover:text-blue-600 rounded" title="Tambah Tugas ke Kategori ini">
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={() => handleDeleteCategory(category.id, category.name)} className="p-1 text-slate-400 hover:text-red-600 rounded" title="Hapus Kategori">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td colSpan={daysInMonth.length} className="border-b border-slate-200 dark:border-slate-700"></td>
                      </tr>
                      
                      {/* Tasks */}
                      {category.tasks && Array.isArray(category.tasks) && category.tasks.map((task) => (
                        <tr key={task.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group">
                          <td className="sticky left-0 z-10 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/80 py-3 px-4 border-b border-r border-slate-200 dark:border-slate-700 font-medium text-slate-700 dark:text-slate-300 group/task">
                            <div className="flex items-center justify-between pl-6">
                              <span>{task.name}</span>
                              <button onClick={() => handleDeleteTask(task.id, task.name)} className="opacity-0 group-hover/task:opacity-100 p-1 text-slate-400 hover:text-red-600 rounded" title="Hapus Tugas">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                          {daysInMonth.map(day => {
                            const dateStr = format(day, "yyyy-MM-dd");
                            const log = task.logs && task.logs[dateStr];
                            const isChecked = !!log;
                            const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                            const today = isToday(day);
                            const isFuture = day > new Date();
                            
                            return (
                              <td 
                                key={dateStr} 
                                className={`border-b border-slate-100 dark:border-slate-700/50 p-1 text-center relative
                                  ${isWeekend ? 'bg-slate-50/50 dark:bg-slate-800/20' : ''}
                                  ${today ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}
                                `}
                              >
                                <div className="flex justify-center items-center h-full w-full">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => !isFuture && requestToggle(task.id, dateStr, task.name)}
                                        disabled={isFuture || isChecked}
                                        className={`w-6 h-6 rounded-md flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-offset-slate-900
                                          ${isFuture || isChecked ? 'cursor-default' : 'cursor-pointer hover:scale-110'}
                                          ${isFuture && !isChecked ? 'opacity-30' : ''}
                                          ${isChecked 
                                            ? 'bg-blue-600 text-white shadow-sm dark:bg-blue-500' 
                                            : 'border border-slate-200 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500 text-transparent hover:text-slate-300 dark:hover:text-slate-600'}
                                        `}
                                      >
                                        <Check className="w-4 h-4" strokeWidth={3} />
                                      </button>
                                    </TooltipTrigger>
                                    {isChecked && log && (
                                      <TooltipContent className="text-xs bg-slate-800 text-white border-slate-700">
                                        <div className="font-semibold mb-1">{log.user_name}</div>
                                        <div className="text-slate-300">
                                          {format(new Date(log.created_at), "MMM d, HH:mm")}
                                        </div>
                                      </TooltipContent>
                                    )}
                                  </Tooltip>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </TooltipProvider>
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Tambah Tugas Rutin</DialogTitle>
            <DialogDescription>
              Buat tugas baru untuk dilacak setiap hari. Anda bisa menggunakan nama kategori yang sudah ada atau membuat yang baru.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4 pt-4">
            <div className="space-y-2 flex flex-col">
              <Label htmlFor="category">Kategori</Label>
              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={popoverOpen}
                    className="w-full justify-between uppercase"
                  >
                    {form.category_name ? form.category_name : "Pilih atau ketik kategori..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[375px] p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Cari atau ketik kategori baru..." 
                      value={form.category_name}
                      onValueChange={(val) => setForm({ ...form, category_name: val.toUpperCase() })}
                    />
                    <CommandList>
                      <CommandEmpty>
                        <div className="p-2 text-sm text-slate-500 flex flex-col items-center gap-2">
                          <p>Tidak ada kategori yang cocok.</p>
                          <Button 
                            size="sm" 
                            variant="secondary"
                            onClick={() => {
                              setPopoverOpen(false);
                            }}
                          >
                            Gunakan "{form.category_name}" sebagai kategori baru
                          </Button>
                        </div>
                      </CommandEmpty>
                      <CommandGroup heading="Kategori Tersedia">
                        {routines.map((cat) => (
                          <CommandItem
                            key={cat.id}
                            value={cat.name}
                            onSelect={(currentValue) => {
                              setForm({ ...form, category_name: currentValue.toUpperCase() });
                              setPopoverOpen(false);
                            }}
                            className="uppercase"
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                form.category_name.toUpperCase() === cat.name.toUpperCase() ? "opacity-100" : "opacity-0"
                              }`}
                            />
                            {cat.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-slate-500">Pilih kategori yang sudah ada atau ketik untuk membuat baru.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="task">Nama Tugas</Label>
              <Input
                id="task"
                placeholder="misal: Cek Log Firewall"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoComplete="off"
              />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Batal
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Simpan Tugas
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-blue-600" />
              Konfirmasi Penyelesaian Tugas?
            </DialogTitle>
            <DialogDescription className="pt-3">
              Apakah Anda yakin ingin menandai <strong>{pendingCheck?.taskName}</strong> sebagai selesai untuk tanggal <strong>{pendingCheck && format(new Date(pendingCheck.dateStr), "dd MMMM yyyy")}</strong>?
            </DialogDescription>
            <div className="text-xs text-slate-500 italic mt-2">
              Tindakan ini akan dicatat menggunakan profil akses Anda dan tidak dapat dibatalkan.
            </div>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Batal
            </Button>
            <Button onClick={handleConfirmToggle} className="bg-blue-600 hover:bg-blue-700 text-white">
              Konfirmasi Selesai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}